import {
  Address,
  EnumEventType,
  EnumProductEventMetadataSessionGranularity,
  EnumProductEventMetadataTicketStructure,
  EnumProductTierSessionScope,
  EnumProductType,
  EventByIdQuery,
  EventManyAppQuery,
  ProductFragmentFragment,
} from "gql/generated";
import { buildEventSessionId, expandDateRangeInclusive } from "@shared/events";
import { getRecurrencePatternDates } from "./eventHelpers";

export type EventSessionOption = {
  sessionId: string;
  dateISO: string;
  timeLabel?: string;
  startsAt?: string;
  endsAt?: string;
};

export type EventPurchaseFlow =
  | "quantity"
  | "sessionPicker"
  | "tierSelector";

type EventLike = EventManyAppQuery["eventMany"][number];
type EventProductLike = Pick<ProductFragmentFragment, "type" | "eventMetadata">;

export const isEventProduct = (
  product?: Pick<ProductFragmentFragment, "type"> | null,
): boolean => product?.type === EnumProductType.Event;

type EventForGettingHere = Pick<
  NonNullable<EventByIdQuery["eventById"]>,
  "locationType" | "eventLocations" | "eventOrganizer"
>;

const hasMappableAddress = (address?: Address | null): boolean =>
  !!address?.location?.coordinates?.length &&
  !(
    address.location.coordinates[0] === 0 &&
    address.location.coordinates[1] === 0
  );

/**
 * Venue address for "Getting here" on event orders. In-person events store the
 * actual venue in `eventLocations`; `eventOrganizer` is the host org (often a
 * head office), which matches product/operator contact when useOwnerContact.
 */
export const resolveEventGettingHereAddress = (
  event?: EventForGettingHere | null,
): Address | undefined => {
  if (!event || event.locationType !== "IN_PERSON") return undefined;

  const fromLocation = event.eventLocations?.find(loc =>
    hasMappableAddress(loc?.contact?.address as Address | undefined),
  )?.contact?.address as Address | undefined;
  if (fromLocation) return fromLocation;

  const fromOrganizer = event.eventOrganizer?.contact?.address as
    | Address
    | undefined;
  if (hasMappableAddress(fromOrganizer)) return fromOrganizer;

  return undefined;
};

export const isFiniteEvent = (event?: EventLike | null): boolean =>
  Boolean(event?.endDate || event?.recurrencePattern?.endDate);

type EventDaysLike = {
  type?: EnumEventType | null;
  startDate?: string | null;
  endDate?: string | null;
  occurrences?: (string | null)[] | null;
  recurrencePattern?:
    | ({ frequency?: unknown; multiDates?: (string | null)[] | null } & Record<
        string,
        unknown
      >)
    | null;
};

/**
 * Every calendar day an event covers, derived from its definition. Works without
 * a persisted `occurrences` list (the booker's eventById query doesn't return
 * one), mirroring the admin builder:
 *  - Single: the inclusive start..end span
 *  - Multi: startDate + recurrencePattern.multiDates
 *  - Recurring: expanded from the recurrence pattern (falls back to occurrences)
 */
export const deriveEventDays = (
  event: EventDaysLike | null | undefined,
): string[] => {
  if (!event?.startDate) return [];

  if (event.type === EnumEventType.Multi) {
    const dates = [
      event.startDate,
      ...((event.recurrencePattern?.multiDates ?? []) as (string | null)[]),
    ].filter((date): date is string => Boolean(date));
    return Array.from(new Set(dates)).sort();
  }

  if (event.type === EnumEventType.Recurring) {
    if (event.recurrencePattern?.frequency) {
      try {
        return getRecurrencePatternDates(
          event.recurrencePattern as unknown as Parameters<
            typeof getRecurrencePatternDates
          >[0],
          event.startDate,
        );
      } catch {
        // fall through to occurrences
      }
    }
    return (event.occurrences ?? []).filter((date): date is string =>
      Boolean(date),
    );
  }

  return expandDateRangeInclusive(event.startDate, event.endDate);
};

/**
 * Concrete occurrence dates for an event, used to build sessions. Prefers the
 * derived day set; falls back to a persisted `occurrences` list.
 */
const resolveOccurrenceDates = (event: EventLike): string[] => {
  const derived = deriveEventDays(event as EventDaysLike);
  if (derived.length > 0) return derived;
  return (event.occurrences ?? []).filter(
    (occurrence): occurrence is string => Boolean(occurrence),
  );
};

export const buildEventSessionOptions = (
  event: EventLike | null | undefined,
  granularity:
    | EnumProductEventMetadataSessionGranularity
    | null
    | undefined = EnumProductEventMetadataSessionGranularity.Date,
  /**
   * Override the occurrence dates used to build sessions. The booker derives
   * dates from the future-filtered `occurrences`, but the admin builder needs
   * every day the event covers (including past dates), so it passes the full
   * set computed from the event definition.
   */
  occurrenceDatesOverride?: string[],
): EventSessionOption[] => {
  if (!event) return [];

  // Whole-event: the entire event (span or single day) is one session, and any
  // time slots are ignored - a single ticket/pass covers everything.
  if (granularity === EnumProductEventMetadataSessionGranularity.WholeEvent) {
    return event.startDate
      ? [{ sessionId: event.startDate, dateISO: event.startDate }]
      : [];
  }

  const occurrenceDates = occurrenceDatesOverride ?? resolveOccurrenceDates(event);

  if (granularity !== EnumProductEventMetadataSessionGranularity.TimeSlot) {
    return occurrenceDates.map(dateISO => ({
      sessionId: dateISO,
      dateISO,
    }));
  }

  const timeSlots =
    event.schedules?.flatMap(schedule =>
      (schedule?.timeSlots ?? []).map(slot => ({
        // Stable slot id, matching the backend session-id encoding. Falls back
        // to the start time only if a slot somehow lacks an id.
        timeSlotId: slot?._id ? String(slot._id) : (slot?.from ?? ""),
        startsAt: slot?.from ?? "",
        endsAt: slot?.to ?? "",
        timeLabel:
          slot?.from && slot?.to
            ? `${slot.from}-${slot.to}${slot.endsNextDay ? " (+1)" : ""}`
            : slot?.from ?? slot?.to ?? "",
      })),
    ) ?? [];

  if (timeSlots.length === 0) {
    return occurrenceDates.map(dateISO => ({
      sessionId: dateISO,
      dateISO,
    }));
  }

  return occurrenceDates.flatMap(dateISO =>
    timeSlots.map(slot => ({
      sessionId: buildEventSessionId(dateISO, slot.timeSlotId),
      dateISO,
      startsAt: slot.startsAt,
      endsAt: slot.endsAt,
      timeLabel: slot.timeLabel,
    })),
  );
};

export const getEventPurchaseFlow = (
  product?: EventProductLike | null,
  sessionCount: number = 0,
): EventPurchaseFlow | null => {
  if (!isEventProduct(product)) return null;

  if (
    product?.eventMetadata?.ticketStructure ===
    EnumProductEventMetadataTicketStructure.Packages
  ) {
    return "tierSelector";
  }

  return sessionCount > 1 ? "sessionPicker" : "quantity";
};

export const tierRequiresSessionSelection = (
  scope?: EnumProductTierSessionScope | null,
): boolean => (scope ?? EnumProductTierSessionScope.Single) === EnumProductTierSessionScope.Single;
